import { type FunctionComponent, type PropsWithChildren, useRef } from 'react';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import { ownershipMarkerNullString } from './ownershipMarkerNullString.tsx';
import { ownershipMarkerPrefix } from './ownershipMarkerPrefix.tsx';

export type OwnershipParams = {
	markerType: 'placeholder' | 'error-boundary';
	/**
	 * Uniquely identifies the code location along with {@link markerType}. The meaning of 'name' also depends on {@link markerType}.
	 */
	name: string;
	packageName?: string;
	teamName?: string;
};

const MARKER_COMPONENTS = new Map<string, FunctionComponent<PropsWithChildren>>();

autoBeforeEachTest(() => {
	MARKER_COMPONENTS.clear();
});

const createKeyForComponent = ({ markerType, name, packageName, teamName }: OwnershipParams) => {
	return `${markerType}/${name}/${packageName ?? ownershipMarkerNullString}/${teamName ?? ownershipMarkerNullString}`;
};

/**
 * Creates a stable marker component, used to add debugging information to the React component tree.
 * Multiple calls to this method with the same parameters will produce the exact same component instance.
 *
 * IMPORTANT LIMITATION: this component must remain stable, however the `name` attached to each component instance
 * cannot be modified because it ends up being cached by React. This means this component is only suitable for capturing
 * errors on initial render (e.g. during React hydration), but not after navigating to a different route - this will
 * change the props (unless unmounted) but the owner mark will remain the same.
 */
export const useOwnershipMarkerComponent = (
	nextParams: OwnershipParams,
): FunctionComponent<PropsWithChildren> => {
	// oxlint-disable-next-line react/refs
	const params = useRef(nextParams).current;
	const componentKey = createKeyForComponent(params);
	const ExistingComponent = MARKER_COMPONENTS.get(componentKey);
	if (ExistingComponent) {
		return ExistingComponent;
	}

	const NewComponent = ({ children }: PropsWithChildren) => children;
	const ownershipMarkerName = encodeDisplayName(params);
	Object.defineProperty(NewComponent, 'name', { value: ownershipMarkerName });
	// oxlint-disable-next-line react/immutability
	NewComponent.displayName = ownershipMarkerName;
	MARKER_COMPONENTS.set(componentKey, NewComponent);
	return NewComponent;
};

const encodeDisplayName = ({
	markerType,
	name,
	packageName = ownershipMarkerNullString,
	teamName = ownershipMarkerNullString,
}: OwnershipParams) => {
	return `${ownershipMarkerPrefix}${markerType}___${name}___${packageName}___${teamName}`;
};
